import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { WalletPaymentProvider } from '@model/WalletPaymentProvider'

import { safeSessionStorage } from '@hooks/useStorage'

import { DepositTarget } from '../../hooks/types'
import { isRealPspId } from '../helpers'
import type { DepositFlowLocationState } from '../types'

const providerCacheKey = (target: DepositTarget, id: string, pspId?: string) =>
  `deposit:selected-provider:${target}:${id}:${pspId || 'none'}`

const providerCacheKeyAny = (target: DepositTarget, id: string) =>
  `deposit:selected-provider:${target}:${id}:any`

const sameProvider = (a?: WalletPaymentProvider, b?: WalletPaymentProvider) => {
  if (a === b) return true
  if (!a || !b) return false
  return a.method?.id === b.method?.id && a.currency?.id === b.currency?.id
}

export const useDepositProviderCache = (params: {
  target?: DepositTarget
  id?: string
  pspId?: string
  locationState: DepositFlowLocationState
}) => {
  const { target, id, pspId, locationState } = params

  const selectionState = useMemo(
    () => ({
      returnPath: locationState.returnPath,
      isDestinationPreselected: locationState.isDestinationPreselected,
      selectedWalletId: locationState.selectedWalletId,
      tradingAccountId: locationState.tradingAccountId,
    }),
    [
      locationState.returnPath,
      locationState.isDestinationPreselected,
      locationState.selectedWalletId,
      locationState.tradingAccountId,
    ]
  )

  const cachedProviderExact = useMemo(() => {
    if (!target || !id) return undefined
    return (
      safeSessionStorage.getJSON<WalletPaymentProvider>(providerCacheKey(target, id, pspId)) ??
      undefined
    )
  }, [target, id, pspId])

  const cachedProviderAny = useMemo(() => {
    if (!target || !id) return undefined
    return (
      safeSessionStorage.getJSON<WalletPaymentProvider>(providerCacheKeyAny(target, id)) ??
      undefined
    )
  }, [target, id])

  const initialProvider = useMemo(() => {
    const fromLocation = locationState.walletPaymentProvider
    const needsMatch = isRealPspId(pspId)

    if (needsMatch) {
      if (fromLocation?.method?.id === pspId) return fromLocation
      if (cachedProviderExact?.method?.id === pspId) return cachedProviderExact
      if (cachedProviderAny?.method?.id === pspId) return cachedProviderAny
      return undefined
    }

    return fromLocation ?? cachedProviderExact ?? cachedProviderAny
  }, [cachedProviderAny, cachedProviderExact, locationState.walletPaymentProvider, pspId])

  const [walletPaymentProvider, setWalletPaymentProviderState] = useState<
    WalletPaymentProvider | undefined
  >(initialProvider)

  const identityRef = useRef<string>('')

  useEffect(() => {
    const nextIdentity = `${target || ''}::${id || ''}::${pspId || ''}`
    const identityChanged = identityRef.current !== nextIdentity
    if (identityChanged) {
      identityRef.current = nextIdentity
      setWalletPaymentProviderState(initialProvider)
      return
    }

    if (initialProvider && !sameProvider(walletPaymentProvider, initialProvider)) {
      setWalletPaymentProviderState(initialProvider)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, initialProvider, pspId, target])

  const setWalletPaymentProvider = useCallback(
    (next?: WalletPaymentProvider) => {
      if (!next) {
        setWalletPaymentProviderState((prev) => prev)
        return
      }

      if (isRealPspId(pspId) && next.method?.id !== pspId) {
        return
      }

      setWalletPaymentProviderState((prev) => (sameProvider(prev, next) ? prev : next))

      if (!target || !id) return

      safeSessionStorage.setJSON(providerCacheKeyAny(target, id), next)
      safeSessionStorage.setJSON(providerCacheKey(target, id, pspId), next)
    },
    [id, pspId, target]
  )

  return {
    walletPaymentProvider,
    setWalletPaymentProvider,
    selectionState,
  }
}
